import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useTransform, useSpring } from "framer-motion";
import {
  ArrowRight,
  MessageCircle,
  Sparkles,
  TrendingUp,
  CheckCircle2,
  Briefcase,
  Linkedin,
  FileText,
  Activity,
} from "lucide-react";
import { LineChart, Line, ResponsiveContainer, AreaChart, Area } from "recharts";
import { WHATSAPP_URL } from "@/lib/site-data";
import PlacementTicker from "@/components/PlacementTicker";

const sparkData = [
  { v: 12 }, { v: 18 }, { v: 16 }, { v: 24 }, { v: 30 }, { v: 38 },
  { v: 42 }, { v: 55 }, { v: 60 }, { v: 72 }, { v: 80 }, { v: 92 },
];

function CircularScore({ score = 87, label = "Resume Score" }) {
  const r = 36;
  const c = 2 * Math.PI * r;
  const dash = (score / 100) * c;
  return (
    <div className="flex items-center gap-4">
      <div className="relative w-24 h-24">
        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
          <circle cx="50" cy="50" r={r} stroke="rgba(255,255,255,0.06)" strokeWidth="8" fill="none" />
          <circle
            cx="50"
            cy="50"
            r={r}
            stroke="url(#grad)"
            strokeWidth="8"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={`${dash} ${c}`}
          />
          <defs>
            <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#5b78ff" />
              <stop offset="100%" stopColor="#8A2BE2" />
            </linearGradient>
          </defs>
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="font-heading text-2xl font-semibold text-white">{score}</span>
        </div>
      </div>
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-[#9db1ff]">{label}</p>
        <p className="text-sm text-zinc-400 mt-1">Excellent · ATS-ready</p>
      </div>
    </div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { damping: 18 });

  const onMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  return (
    <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden" data-testid="hero-section">
      {/* Background glows - decorative, behind content */}
      <div aria-hidden className="absolute inset-0 pointer-events-none z-0">
        <div
          className="absolute -top-32 -left-40 w-[640px] h-[640px] rounded-full opacity-60"
          style={{ background: "radial-gradient(circle, rgba(43,77,255,0.45), transparent 70%)", filter: "blur(120px)" }}
        />
        <div
          className="absolute top-40 -right-40 w-[560px] h-[560px] rounded-full opacity-50"
          style={{ background: "radial-gradient(circle, rgba(138,43,226,0.5), transparent 70%)", filter: "blur(120px)" }}
        />
        <div
          className="absolute inset-0 opacity-[0.12]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
            maskImage: "radial-gradient(ellipse at center, black 30%, transparent 70%)",
            WebkitMaskImage: "radial-gradient(ellipse at center, black 30%, transparent 70%)",
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Copy */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.08] text-xs text-zinc-300">
            <Sparkles className="w-3.5 h-3.5 text-[#5b78ff]" />
            AI-powered US career acceleration
          </div>

          <h1 className="mt-6 font-heading text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter leading-[1.02] text-white">
            Get Hired In The{" "}
            <span className="relative inline-block">
              <span className="text-gradient-electric">USA</span>
              <span
                aria-hidden
                className="absolute -inset-2 -z-10 blur-2xl opacity-60"
                style={{ background: "radial-gradient(ellipse, rgba(43,77,255,0.35), transparent 70%)" }}
              />
            </span>{" "}
            Faster.
          </h1>

          <p className="mt-6 text-lg text-zinc-400 max-w-xl leading-relaxed">
            Xyro Tech Solutions helps international students, OPT/CPT candidates, H1B professionals
            and IT experts land high-paying US roles through AI-powered resume optimization,
            interview prep and placement support.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/contact"
              data-testid="hero-book-cta"
              className="btn-primary-xyro inline-flex items-center gap-2 rounded-full px-7 py-4 font-medium"
            >
              Book Free Career Consultation
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-whatsapp-cta"
              className="btn-ghost-xyro inline-flex items-center gap-2 rounded-full px-7 py-4 font-medium"
            >
              <MessageCircle className="w-4 h-4 text-emerald-400" /> Chat On WhatsApp
            </a>
          </div>

          <div className="mt-6">
            <PlacementTicker />
          </div>

          <div className="mt-8 grid grid-cols-3 gap-6 max-w-md">
            {[
              { v: "1,240+", l: "Students placed" },
              { v: "94%", l: "Success rate" },
              { v: "$105k", l: "Avg. package" },
            ].map((s) => (
              <div key={s.l}>
                <p className="font-heading text-2xl md:text-3xl font-semibold text-white">{s.v}</p>
                <p className="text-xs text-zinc-500 mt-1 leading-tight">{s.l}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* 3D Dashboard mockup */}
        <motion.div
          ref={ref}
          onMouseMove={onMove}
          onMouseLeave={onLeave}
          style={{ perspective: 1200 }}
          className="relative w-full max-w-full"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          data-testid="hero-dashboard"
        >
          <motion.div
            style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
            className="grid grid-rows-[auto_auto] gap-3 sm:gap-4"
          >
            {/* Main card */}
            <motion.div
              style={{ transform: "translateZ(40px)" }}
              className="relative z-10 glass-prism p-5 sm:p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                  <p className="text-xs text-zinc-400 font-mono">xyro.ai / dashboard</p>
                </div>
                <p className="text-xs text-zinc-500 font-mono">v3.2</p>
              </div>
              <CircularScore score={87} label="AI Resume Score" />
              <div className="mt-5 grid grid-cols-3 gap-2 sm:gap-3">
                <div className="p-2.5 sm:p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                  <FileText className="w-4 h-4 text-[#5b78ff] mb-2" />
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500">ATS</p>
                  <p className="text-white font-semibold text-sm">94/100</p>
                </div>
                <div className="p-2.5 sm:p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                  <Linkedin className="w-4 h-4 text-[#5b78ff] mb-2" />
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500">LinkedIn</p>
                  <p className="text-white font-semibold text-sm">82/100</p>
                </div>
                <div className="p-2.5 sm:p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                  <Briefcase className="w-4 h-4 text-[#5b78ff] mb-2" />
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500">Jobs</p>
                  <p className="text-white font-semibold text-sm">128</p>
                </div>
              </div>
            </motion.div>

            {/* Lower row: 2-column grid (Pipeline + Interviews) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 min-h-0">
              {/* Pipeline mini card */}
              <motion.div
                style={{ transform: "translateZ(70px)" }}
                className="relative z-10 glass-prism p-4 flex flex-col"
              >
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-zinc-400">Application Pipeline</p>
                  <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
                </div>
                <div className="h-[80px] sm:h-[90px] -mx-1">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={sparkData}>
                      <defs>
                        <linearGradient id="ag" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#5b78ff" stopOpacity={0.7} />
                          <stop offset="100%" stopColor="#5b78ff" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <Area type="monotone" dataKey="v" stroke="#5b78ff" strokeWidth={2} fill="url(#ag)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-white/[0.05]">
                  <p className="text-white font-heading text-lg font-semibold">+38%</p>
                  <p className="text-[10px] text-zinc-500">vs last month</p>
                </div>
              </motion.div>

              {/* Interviews + Offer (combined card so they never overlap) */}
              <motion.div
                style={{ transform: "translateZ(85px)" }}
                className="relative z-10 glass-prism p-4 flex flex-col"
              >
                <p className="text-xs text-zinc-400 mb-2">Upcoming Interviews</p>
                <div className="flex-1 min-h-0">
                  {[
                    { c: "Google", r: "ML Engineer", t: "Tue 10:30" },
                    { c: "Stripe", r: "Data Scientist", t: "Wed 14:00" },
                  ].map((i) => (
                    <div key={i.c} className="flex items-center justify-between py-1.5 border-b border-white/[0.05] last:border-0">
                      <div className="min-w-0">
                        <p className="text-xs text-white font-medium truncate">{i.c}</p>
                        <p className="text-[10px] text-zinc-500 truncate">{i.r}</p>
                      </div>
                      <p className="text-[10px] text-[#9db1ff] font-mono ml-2 whitespace-nowrap">{i.t}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-white/[0.06] flex items-center gap-2.5">
                  <div className="w-8 h-8 flex-none rounded-full bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] text-zinc-400">New offer</p>
                    <p className="text-white text-xs font-semibold truncate">$135,000 / yr</p>
                  </div>
                  <Activity className="w-3 h-3 text-[#5b78ff] flex-none" />
                </div>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
